// Value formatting and cleaning utilities for college data

/**
 * Clean and format ranking values
 * Input: "22", "#22", "##22" -> Output: "#22"
 */
export const cleanRanking = (value) => {
  if (!value || value === '') return '';
  
  // Convert to string and trim
  const str = String(value).trim();
  
  // Extract just the number part
  const numericValue = str.replace(/[^\d]/g, '');
  
  // Return formatted with single #
  return numericValue ? `#${numericValue}` : '';
};

/**
 * Clean and format percentage values
 * Input: "42", "42%", "42%%" -> Output: "42%"
 */
export const cleanPercentage = (value) => {
  if (!value || value === '') return '';
  
  // Convert to string and trim
  const str = String(value).trim();
  
  // Extract just the numeric part (including decimals)
  const numericValue = str.replace(/[^\d.]/g, '');
  
  // Return formatted with single %
  return numericValue ? `${numericValue}%` : '';
};

/**
 * Clean and format currency values
 * Input: "55000", "$55000", "$$55000" -> Output: "$55,000"
 */
export const cleanCurrency = (value) => {
  if (!value || value === '') return '';
  
  // Convert to string and trim
  const str = String(value).trim();
  
  // Extract just the numeric part
  const numericValue = str.replace(/[^\d]/g, '');
  
  if (!numericValue) return '';
  
  // Convert to number and format with commas
  const number = parseInt(numericValue);
  return `$${number.toLocaleString()}`;
};

/**
 * Clean numeric values (remove any non-numeric characters except decimal points)
 * Input: "4.7%", "4.7", "4.7.0" -> Output: "4.7"
 */
export const cleanNumeric = (value) => {
  if (!value || value === '') return '';
  
  // Convert to string and trim
  const str = String(value).trim();
  
  // Extract numeric value with decimal points
  const numericValue = str.replace(/[^\d.]/g, '');
  
  // Handle multiple decimal points (keep only the first one)
  const parts = numericValue.split('.');
  if (parts.length > 2) {
    return `${parts[0]}.${parts.slice(1).join('')}`;
  }
  
  return numericValue;
};

/**
 * Clean test score ranges
 * Input: "1400-1560", "1400 - 1560", "1400 to 1560" -> Output: "1400-1560"
 */
export const cleanTestScoreRange = (value) => {
  if (!value || value === '') return '';
  
  // Convert to string and trim
  const str = String(value).trim();
  
  // Extract numbers
  const numbers = str.match(/\d+/g);
  
  if (!numbers || numbers.length < 2) {
    // If only one number, treat as single score
    return numbers && numbers[0] ? numbers[0] : '';
  }
  
  // Return as range
  return `${numbers[0]}-${numbers[1]}`;
};

/**
 * Handle JSONB field formatting
 */
export const formatJSONBField = (fieldName, value) => {
  if (!value) return null;
  
  // If it's already an object/array, return as-is
  if (typeof value === 'object') return value;
  
  // If it's a string, try to parse it
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (e) {
      // If it's not valid JSON, handle as array/string
      const normalizedField = fieldName.toLowerCase();
      
      if (normalizedField.includes('prompts') || normalizedField.includes('platforms') || normalizedField.includes('advantages')) {
        // Split by commas and clean up
        return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
      }
      
      return value;
    }
  }
  
  return value;
};

/**
 * Parse JSONB field back to storage format
 */
export const parseJSONBField = (fieldName, value) => {
  if (!value) return null;
  
  // If it's already an object/array, stringify it
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  
  // If it's a string that looks like a list
  if (typeof value === 'string') {
    const normalizedField = fieldName.toLowerCase();
    
    if (normalizedField.includes('prompts') || normalizedField.includes('platforms') || normalizedField.includes('advantages')) {
      // Convert comma-separated string to array
      const items = value.split(',').map(item => item.trim()).filter(item => item.length > 0);
      return JSON.stringify(items);
    }
    
    // Try to parse as JSON first
    try {
      JSON.parse(value);
      return value; // It's already valid JSON
    } catch (e) {
      return JSON.stringify(value); // Wrap in quotes
    }
  }
  
  return JSON.stringify(value);
};

/**
 * Auto-format field values based on field type
 */
export const autoFormatField = (fieldName, value) => {
  if (!value || value === '') return '';
  
  const normalizedField = fieldName.toLowerCase();
  
  // Handle JSONB fields
  if (normalizedField.includes('prompts') || normalizedField.includes('platforms') || 
      normalizedField.includes('advantages') || normalizedField.includes('limits')) {
    const formatted = formatJSONBField(fieldName, value);
    if (Array.isArray(formatted)) {
      return formatted.join(', ');
    }
    if (typeof formatted === 'object') {
      return JSON.stringify(formatted, null, 2);
    }
    return formatted;
  }
  
  // Ranking fields
  if (normalizedField.includes('ranking') || normalizedField.includes('rank')) {
    return cleanRanking(value);
  }
  
  // Percentage fields
  if (normalizedField.includes('rate') || normalizedField.includes('percent') || normalizedField.includes('ratio')) {
    return cleanPercentage(value);
  }
  
  // Currency fields
  if (normalizedField.includes('tuition') || normalizedField.includes('cost') || normalizedField.includes('fee') || normalizedField.includes('salary') || normalizedField.includes('board') || normalizedField.includes('room')) {
    return cleanCurrency(value);
  }
  
  // Test score range fields
  if (normalizedField.includes('sat') && normalizedField.includes('range')) {
    return cleanTestScoreRange(value);
  }
  
  if (normalizedField.includes('act') && normalizedField.includes('range')) {
    return cleanTestScoreRange(value);
  }
  
  // GPA fields (numeric only)
  if (normalizedField.includes('gpa')) {
    return cleanNumeric(value);
  }
  
  // Fit score fields (numeric with decimal)
  if (normalizedField.includes('fit_score') || normalizedField.includes('score')) {
    return cleanNumeric(value);
  }
  
  // Date fields (already formatted by browser date input)
  if (normalizedField.includes('deadline') || normalizedField.includes('date')) {
    return value; // Date inputs handle their own formatting
  }
  
  // Default: return as-is
  return value;
};

/**
 * Parse display value back to storage value
 * Used when saving data to database
 */
export const parseDisplayValue = (fieldName, displayValue) => {
  if (!displayValue || displayValue === '') return null;
  
  // Ensure displayValue is a string for .replace() operations
  if (typeof displayValue !== 'string') {
    displayValue = String(displayValue);
  }
  
  const normalizedField = fieldName.toLowerCase();
  
  // Handle JSONB fields
  if (normalizedField.includes('prompts') || normalizedField.includes('platforms') || 
      normalizedField.includes('advantages') || normalizedField.includes('limits')) {
    return parseJSONBField(fieldName, displayValue);
  }
  
  // For percentage fields, store as number
  if (normalizedField.includes('rate') || normalizedField.includes('percent')) {
    const numericValue = displayValue.replace(/[^\d.]/g, '');
    return numericValue ? parseFloat(numericValue) : null;
  }
  
  // For ranking fields, store as number
  if (normalizedField.includes('ranking') || normalizedField.includes('rank')) {
    const numericValue = displayValue.replace(/[^\d]/g, '');
    return numericValue ? parseInt(numericValue) : null;
  }
  
  // For currency fields, store as number
  if (normalizedField.includes('tuition') || normalizedField.includes('cost') || normalizedField.includes('fee') || normalizedField.includes('salary') || normalizedField.includes('board') || normalizedField.includes('room')) {
    const numericValue = displayValue.replace(/[^\d]/g, '');
    return numericValue ? parseInt(numericValue) : null;
  }
  
  // For GPA fields, store as number
  if (normalizedField.includes('gpa')) {
    return displayValue ? parseFloat(displayValue) : null;
  }
  
  // For fit score fields, store as number
  if (normalizedField.includes('fit_score') || normalizedField.includes('score')) {
    return displayValue ? parseFloat(displayValue) : null;
  }
  
  // For test score ranges, store as string
  if ((normalizedField.includes('sat') || normalizedField.includes('act')) && normalizedField.includes('range')) {
    return displayValue;
  }
  
  // For date fields, store as string (YYYY-MM-DD format)
  if (normalizedField.includes('deadline') || normalizedField.includes('date')) {
    return displayValue; // Date inputs provide proper format
  }
  
  // For boolean fields (visited)
  if (normalizedField.includes('visited') || typeof displayValue === 'boolean') {
    return Boolean(displayValue);
  }
  
  // Default: return as-is
  return displayValue;
};

/**
 * Format value for display (from database value)
 */
export const formatForDisplay = (fieldName, dbValue) => {
  if (dbValue === null || dbValue === undefined || dbValue === '') return '';
  
  const normalizedField = fieldName.toLowerCase();
  
  // For percentage fields, convert decimals to percentages and add %
  if (normalizedField.includes('rate') || normalizedField.includes('percent')) {
    const numValue = parseFloat(dbValue);
    if (isNaN(numValue)) return '';
    
    // If value is less than 1, treat as decimal and convert to percentage
    // If value is >= 1, treat as already a percentage value
    const percentValue = numValue < 1 ? (numValue * 100).toFixed(1) : numValue;
    return `${percentValue}%`;
  }
  
  // For ranking fields, add # if not present
  if (normalizedField.includes('ranking') || normalizedField.includes('rank')) {
    return `#${dbValue}`;
  }
  
  // For currency fields, format with $ and commas
  if (normalizedField.includes('tuition') || normalizedField.includes('cost') || normalizedField.includes('fee') || normalizedField.includes('salary') || normalizedField.includes('board') || normalizedField.includes('room')) {
    return `$${parseInt(dbValue).toLocaleString()}`;
  }
  
  // Default: return as-is
  return String(dbValue);
};